/**
 * Pairing UI - QR Code and Connection Status
 *
 * Displays pairing QR code and session code for phone connection.
 * Manages PairingManager for reader role.
 *
 * All styling uses tokens.css variables. Cyan accent is focus/active only.
 */

import QRCode from 'qrcode';
import { type ReaderCore } from '../../core/readerCore';
import {
  buildPhoneUrl,
  isSupabaseConfigured,
  createReaderPairing,
  type PairingManager,
  type PairingState,
} from '../../pairing';

/**
 * Pairing UI elements
 */
interface PairingElements {
  container: HTMLElement;
  qrCanvas: HTMLCanvasElement;
  sessionCodeDisplay: HTMLElement;
  statusIndicator: HTMLElement;
  statusText: HTMLElement;
}

/**
 * Pairing UI state
 */
interface PairingUIState {
  sessionCode: string;
  manager: PairingManager | null;
  unsubscribe: (() => void) | null;
}

// Module state
let pairingState: PairingUIState | null = null;
let pairingElements: PairingElements | null = null;

/**
 * Initialize pairing UI and connect to ReaderCore
 *
 * @param parentContainer - Container to append pairing UI to
 * @param core - ReaderCore to control via remote commands
 * @param sessionCode - Session code to use (shared with landing page QR)
 * @returns Cleanup function
 */
export function initPairingUI(
  parentContainer: HTMLElement,
  core: ReaderCore,
  sessionCode: string
): () => void {
  // Check if Supabase is configured
  if (!isSupabaseConfigured()) {
    console.warn('Supabase not configured - pairing disabled');
    return () => {};
  }

  // Create UI elements
  const els = createPairingElements(parentContainer);
  pairingElements = els;

  // Store state
  pairingState = {
    sessionCode,
    manager: null,
    unsubscribe: null,
  };

  // Display session code
  els.sessionCodeDisplay.textContent = sessionCode;

  // Generate and display QR code
  const phoneUrl = buildPhoneUrl(window.location.origin, sessionCode);
  generateQRCode(els.qrCanvas, phoneUrl);

  // Create and start pairing manager
  startPairing(sessionCode, core);

  // Return cleanup function
  return () => cleanupPairingUI();
}

/**
 * Create pairing UI DOM elements
 */
function createPairingElements(parent: HTMLElement): PairingElements {
  const container = document.createElement('div');
  container.className = 'pairing-panel';

  // Header
  const header = document.createElement('div');
  header.className = 'pairing-header';

  const title = document.createElement('span');
  title.className = 'pairing-title';
  title.textContent = 'Connect Phone';
  header.appendChild(title);

  // Status indicator
  const statusIndicator = document.createElement('span');
  statusIndicator.className = 'pairing-status-indicator';

  const statusDot = document.createElement('span');
  statusDot.className = 'status-dot';
  statusIndicator.appendChild(statusDot);

  const statusText = document.createElement('span');
  statusText.className = 'status-text';
  statusText.textContent = 'Waiting...';
  statusIndicator.appendChild(statusText);

  header.appendChild(statusIndicator);
  container.appendChild(header);

  // QR code canvas
  const qrContainer = document.createElement('div');
  qrContainer.className = 'qr-container';

  const qrCanvas = document.createElement('canvas');
  qrCanvas.className = 'qr-canvas';
  qrCanvas.width = 160;
  qrCanvas.height = 160;
  qrContainer.appendChild(qrCanvas);

  container.appendChild(qrContainer);

  // Session code display
  const codeContainer = document.createElement('div');
  codeContainer.className = 'session-code-container';

  const codeLabel = document.createElement('span');
  codeLabel.className = 'session-code-label';
  codeLabel.textContent = 'Code: ';
  codeContainer.appendChild(codeLabel);

  const sessionCodeDisplay = document.createElement('span');
  sessionCodeDisplay.className = 'session-code';
  codeContainer.appendChild(sessionCodeDisplay);

  container.appendChild(codeContainer);

  parent.appendChild(container);

  return {
    container,
    qrCanvas,
    sessionCodeDisplay,
    statusIndicator,
    statusText,
  };
}

/**
 * Read CSS token value
 */
function getCSSToken(name: string, fallback: string): string {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

/**
 * Generate QR code on canvas
 */
async function generateQRCode(canvas: HTMLCanvasElement, url: string): Promise<void> {
  try {
    // Read colors from CSS tokens (QR library requires explicit color strings)
    const foreground = getCSSToken('--qr-foreground', '#ffffff');
    const background = getCSSToken('--qr-background', 'transparent');

    await QRCode.toCanvas(canvas, url, {
      width: 160,
      margin: 1,
      color: {
        dark: foreground,
        light: background === 'transparent' ? '#00000000' : background,
      },
    });
  } catch (err) {
    console.error('Failed to generate QR code:', err);
  }
}

/**
 * Start pairing manager and wire up events
 */
async function startPairing(sessionCode: string, core: ReaderCore): Promise<void> {
  if (!pairingState) return;

  const manager = createReaderPairing(sessionCode);
  pairingState.manager = manager;

  // Subscribe to pairing events
  pairingState.unsubscribe = manager.on((event) => {
    switch (event.type) {
      case 'stateChange':
        updateStatusDisplay(event.state);
        break;

      case 'textReceived':
        // Load received text into ReaderCore
        core.setText(event.text);
        break;

      case 'commandReceived':
        // Execute transport command on ReaderCore
        handleRemoteCommand(core, event.command, event.words);
        break;

      case 'wpmReceived':
        // Update WPM on ReaderCore
        core.setWpm(event.wpm);
        break;

      case 'error':
        console.error('Pairing error:', event.error);
        break;
    }
  });

  // Connect to channel
  try {
    await manager.connect();
  } catch (err) {
    console.error('Failed to connect pairing:', err);
    updateStatusDisplay('disconnected');
  }
}

/**
 * Handle remote transport command
 */
function handleRemoteCommand(
  core: ReaderCore,
  command: string,
  words?: number
): void {
  switch (command) {
    case 'play':
      core.play();
      break;
    case 'pause':
      core.pause();
      break;
    case 'rewind':
      core.rewind(words);
      break;
    case 'fastForward':
      core.fastForward(words);
      break;
    case 'restart':
      core.restart();
      break;
  }
}

/**
 * Update status display based on pairing state
 */
function updateStatusDisplay(state: PairingState): void {
  if (!pairingElements) return;

  const { statusIndicator, statusText } = pairingElements;

  // Remove all state classes
  statusIndicator.classList.remove('waiting', 'connected', 'disconnected');

  switch (state) {
    case 'connecting':
    case 'waiting':
      statusIndicator.classList.add('waiting');
      statusText.textContent = 'Waiting...';
      break;
    case 'connected':
      statusIndicator.classList.add('connected');
      statusText.textContent = 'Connected';
      break;
    case 'disconnected':
      statusIndicator.classList.add('disconnected');
      statusText.textContent = 'Disconnected';
      break;
  }
}

/**
 * Check if pairing is connected
 */
export function isPairingConnected(): boolean {
  return pairingState?.manager?.isConnected ?? false;
}

/**
 * Get current pairing state
 */
export function getPairingState(): PairingState | null {
  return pairingState?.manager?.currentState ?? null;
}

/**
 * Cleanup pairing UI and disconnect
 */
export async function cleanupPairingUI(): Promise<void> {
  if (pairingState) {
    // Unsubscribe from events
    if (pairingState.unsubscribe) {
      pairingState.unsubscribe();
    }

    // Disconnect manager
    if (pairingState.manager) {
      await pairingState.manager.disconnect();
    }

    pairingState = null;
  }

  // Remove UI elements
  if (pairingElements) {
    pairingElements.container.remove();
    pairingElements = null;
  }
}
